import { errorFromResponse, networkError, readJson } from "./httpError";
import { referralParams } from "./referralAttribution";
import { clearRequestCache, withCache } from "./requestCache";

/** Pre-registration and maintenance state, from /api/auth/me and /api/t/config. */
export interface MaintenanceInfo {
  active: boolean;
  id: string | null;
  title: string;
  message: string;
  planned_start: string | null;
  planned_end: string | null;
  /** Announced ahead of time, not started yet. */
  announced: boolean;
}
export interface AccessInfo {
  prelaunch: boolean;
  prelaunch_title: string;
  prelaunch_message: string;
  maintenance: MaintenanceInfo;
  /** This account has to wait for launch (not staff, no early access). */
  prelaunch_blocked?: boolean;
  /** Changes are refused for this account right now. */
  maintenance_blocked?: boolean;
}

const API_BASE =
  process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:8000";

const TOKEN_KEY = "rankbist_token";

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null; // storage blocked (private mode, strict settings)
  }
}

export function setToken(token: string) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* storage blocked: the sign-in lasts for this page only */
  }
  clearRequestCache();
}

export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("rb_me_cache"); // the saved account picture (components/app/AppContext.tsx)
    localStorage.removeItem("rb_site_visitor"); // the website navbar's saved name and picture (NavAccount.tsx)
  } catch {
    /* nothing stored */
  }
  // The root layout marks <html data-auth="1"> on page load when a sign-in is stored, and moving to the website
  // after signing out doesn't reload the page. Left in place, the navbar kept the avatar placeholder (an endless
  // skeleton) instead of "Log in / Sign up". Signing out clears it here, wherever it happens.
  try {
    if (typeof document !== "undefined") delete document.documentElement.dataset.auth;
  } catch {
    /* no document */
  }
  clearRequestCache();
}

const WORKSPACE_KEY = "rb_workspace";

/** The workspace this browser is working in ("" = your own account). */
/**
 * Plan & billing belong to the main (parent) account only: the account you sign in to, working in its own
 * primary workspace. In any other workspace (one you were invited to, or one of your client workspaces)
 * billing pages, package badges and upgrade buttons are hidden; the API refuses billing there anyway.
 */
export function canManageBilling(): boolean {
  return getWorkspace() === "";
}

export function getWorkspace(): string {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem(WORKSPACE_KEY) ?? "";
  } catch {
    return "";
  }
}

export function setWorkspace(id: string) {
  clearRequestCache();
  try {
    if (id) window.localStorage.setItem(WORKSPACE_KEY, id);
    else window.localStorage.removeItem(WORKSPACE_KEY);
  } catch {
    /* private mode: stays on your own account */
  }
}

/** Sign-in and workspace headers for requests that don't go through `request` (uploads, downloads). */
function authHeaders(): Record<string, string> {
  const token = getToken();
  const workspace = getWorkspace();
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(token && workspace ? { "X-Workspace": workspace } : {}) };
}

/** For streamed responses (live notifications) that can't go through `request`. */
export const apiBaseUrl = API_BASE;
export function apiAuthHeaders(): Record<string, string> {
  return authHeaders();
}

/** Reads go through the one-minute cache; any change empties it (see requestCache.ts). */
function request<T>(path: string, init?: RequestInit): Promise<T> {
  return withCache(path, init, () => requestRaw<T>(path, init));
}

async function requestRaw<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const workspace = getWorkspace();
  const send = () =>
    fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(token && workspace ? { "X-Workspace": workspace } : {}),
        ...(init?.headers ?? {}),
      },
    });
  let res: Response;
  try {
    res = await send();
  } catch (e) {
    if ((e as Error)?.name === "AbortError") throw e;
    // Weak mobile connections drop a request now and then. A read is safe to ask again, so try once more after a
    // short pause before showing "Couldn't reach Rankbist". Changes (POST/PUT/…) are never repeated.
    const method = (init?.method ?? "GET").toUpperCase();
    const online = typeof navigator === "undefined" || navigator.onLine !== false;
    if (method !== "GET" || !online || init?.signal?.aborted) throw networkError();
    await new Promise((r) => setTimeout(r, 900));
    try {
      res = await send();
    } catch (e2) {
      if ((e2 as Error)?.name === "AbortError") throw e2;
      throw networkError();
    }
  }
  if (res.status === 403 && workspace && (await res.clone().json().catch(() => ({}))).detail === "You don't have access to that workspace.") {
    // Removed from the workspace, or it was closed: go back to your own account.
    setWorkspace("");
    if (typeof window !== "undefined") window.location.assign("/dashboard");
  }
  if (!res.ok) throw await errorFromResponse(res);
  return readJson<T>(res);
}

export interface Factor {
  key: string;
  category: string;
  label: string;
  passed: boolean;
  score: number;
  max: number;
  tip: string;
  value: string | null;
}

export interface DeterministicFix {
  field: string;
  before: string;
  after: string;
  reason: string;
}

export interface SerpBenchmark {
  keyword: string;
  market?: string;
  ready: boolean;
  note: string;
  pages: { rank: number | null; url: string; domain: string | null; title: string; ok: boolean; words: number | null; error: string | null }[];
  your_words: number;
  pages_read?: number;
  median_words?: number;
  words_range?: [number, number];
  coverage?: number;
  covered?: number;
  terms_total?: number;
  verdict?: "strong" | "close" | "behind";
  terms?: { term: string; pages: number; you: boolean }[];
  missing?: string[];
}

export interface CheckResponse {
  id: string;
  overall_score: number;
  word_count: number;
  issue_count: number;
  category_scores: Record<string, number>;
  category_issue_counts: Record<string, number>;
  factors: Factor[] | null;
  deterministic_fixes: DeterministicFix[] | null;
  upgrade_required: boolean;
  /** Which language's rules scored this, and anything to know about that. */
  language?: string;
  language_note?: string | null;
  // Live pages checked without a keyword: the one found on the page.
  focus_keyword?: string | null;
  focus_keyword_detected?: boolean;
  /** "Compare with Google's top 10", once run. */
  benchmark?: SerpBenchmark | null;
  /** This page is also in Monitoring; this check is now its score there too. */
  monitored?: boolean;
  /** AI fixes written for this report, kept with it (also on its share link). */
  ai_fixes?: AiFix[] | null;
  ai_fixes_at?: string | null;
}

export interface CheckInput {
  content: string;
  title?: string;
  meta_title?: string;
  meta_description?: string;
  excerpt?: string;
  focus_keyword?: string;
  secondary_keywords?: string[];
  slug?: string;
  /** Re-check of an earlier check: keeps both as versions of one article. */
  previous_check_id?: string;
  /** "en", "bn", "hi", "id". Left out = recognised from the script. */
  language?: string;
}

export interface UrlCheckInput {
  url: string;
  focus_keyword?: string;
  secondary_keywords?: string[];
  language?: string;
}

export const api = {
  signup: (
    name: string,
    email: string,
    password: string,
    phoneNumber: string,
  ) =>
    request<{ message: string; requires_verification: boolean }>(
      "/api/auth/signup",
      {
        method: "POST",
        body: JSON.stringify({
          name,
          email,
          password,
          phone_number: phoneNumber,
          ...referralParams(),
        }),
      },
    ),
  verifyOtp: (email: string, code: string) =>
    request<{ access_token: string }>("/api/auth/verify-otp", {
      method: "POST",
      body: JSON.stringify({ email, code }),
    }),
  resendOtp: (email: string) =>
    request<{ message: string }>("/api/auth/resend-otp", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  login: (email: string, password: string) =>
    request<{ access_token: string }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  googleLogin: (idToken: string, attribution?: { aff?: string; ref?: string }) =>
    request<{ access_token: string }>("/api/auth/google", {
      method: "POST",
      body: JSON.stringify({ id_token: idToken, ...(attribution ?? {}) }),
    }),
  forgotPassword: (email: string) =>
    request<{ message: string }>("/api/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  resetPassword: (email: string, code: string, newPassword: string) =>
    request<{ message: string }>("/api/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ email, code, new_password: newPassword }),
    }),
  me: () =>
    request<{
      id: string;
      email: string;
      name: string | null;
      picture_url: string | null;
      has_password: boolean;
      is_admin: boolean;
      email_verified: boolean;
      plan_name: string | null;
      paddle_customer_id?: string | null;
      full_detail_enabled?: boolean;
      auto_fix_enabled?: boolean;
      /** The package this account is on now ("Start for free" without a subscription). */
      package_name?: string | null;
      package_slug?: string | null;
      is_free_package?: boolean;
      /** Live feature keys the package includes, e.g. "report.detail". */
      features?: string[];
      /** -1 = unlimited, 0 = not included. */
      limits?: Record<string, number>;
      /** On the highest package (Advanced by default): nothing to upgrade to, no upgrade prompts. */
      top_package?: boolean;
      package_id?: string | null;
      /** Pre-registration and maintenance (admin → Launch & maintenance). */
      access?: AccessInfo;
      /** The person who is signed in (the fields above describe the workspace's account inside a client workspace). */
      person?: { id: string; email: string; name: string | null; picture_url: string | null } | null;
      /** Your role in the workspace you're working in. */
      workspace_role?: string;
      /** Access per area in the workspace you're working in (owner: null). */
      workspace_permissions?: Record<string, "none" | "view" | "edit"> | null;
      /** Built but not usable on this server (needs a paid data provider that isn't set): hidden everywhere. */
      unavailable_features?: string[];
    }>("/api/auth/me"),
  changePassword: (newPassword: string, currentPassword?: string) =>
    request<{ message: string }>("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    }),
  check: (input: CheckInput) =>
    request<CheckResponse>("/api/check", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  /** AI fixes for a saved check (Live URL checker: the server uses the text it read from the page). */
  /** AI fixes for a saved report: the kept ones, or newly written ones (refresh = write them again). */
  aiFixSaved: (checkId: string, refresh = false) => request<{ fixes: AiFix[] }>(`/api/check/${checkId}/ai-fix${refresh ? "?refresh=true" : ""}`, { method: "POST" }),
  aiFix: (input: CheckInput) =>
    request<{ fixes: AiFix[] }>("/api/check/ai-fix", { method: "POST", body: JSON.stringify(input) }),
  getUsage: () => request<UsageItem[]>("/api/usage/mine"),
  checkUrl: (input: UrlCheckInput) =>
    request<CheckResponse>("/api/check/url", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  getCheck: (id: string) => request<CheckResponse>(`/api/check/${id}`),
  startBenchmark: (id: string, keyword: string, market = "us") =>
    request<{ job_id: string }>(`/api/check/${id}/benchmark`, { method: "POST", body: JSON.stringify({ keyword, market }) }),
  getCheckHistory: (limit = 20) =>
    request<CheckHistoryItem[]>(`/api/check/history/mine?limit=${limit}`),
  /** Up to 500 checks with per-category totals, for the Overview's filters. */
  getCheckHistoryDetailed: (limit = 500) =>
    request<CheckHistoryItem[]>(`/api/check/history/mine?limit=${limit}&categories=true`),
  getDashboardStats: () => request<DashboardStats>("/api/check/stats/mine"),
  getCheckAnalytics: () => request<CheckAnalytics>("/api/check/analytics/mine"),

  // --- GSC (rank tracking) ---
  gscStatus: () =>
    request<{ connected: boolean; site_url: string | null }>("/api/gsc/status"),
  gscConnect: () => request<{ url: string }>("/api/gsc/connect"),
  gscSites: () => request<{ sites: string[] }>("/api/gsc/sites"),
  gscSelectSite: (site_url: string) =>
    request<{ ok: boolean }>("/api/gsc/select-site", {
      method: "POST",
      body: JSON.stringify({ site_url }),
    }),
  gscRankings: (days = 28) =>
    request<GscRankings>(`/api/gsc/rankings?days=${days}`),
  gscDisconnect: () =>
    request<{ ok: boolean }>("/api/gsc/disconnect", { method: "POST" }),

  // --- Billing ---
  getPlans: () => request<PlanOut[]>("/api/billing/plans"),
  getPromo: () => request<LaunchOffer>("/api/billing/promo"),
  createCheckout: (priceVersionId: string) =>
    request<CheckoutResponse>("/api/billing/checkout", {
      method: "POST",
      body: JSON.stringify({ price_version_id: priceVersionId }),
    }),
  getMySubscription: () =>
    request<SubscriptionOut | null>("/api/billing/subscription"),
  /** After checkout: asks Paddle for the payment and switches the plan on (doesn't wait for the webhook). */
  confirmCheckout: (transaction_id: string) =>
    request<{ state: "active" | "pending"; reason: string | null }>("/api/billing/confirm", { method: "POST", body: JSON.stringify({ transaction_id }) }),
  cancelSubscription: () =>
    request<{ message: string }>("/api/billing/cancel", { method: "POST" }),
  getBillingPortalUrl: () => request<{ url: string }>("/api/billing/portal"),

  // --- Tools (premium) ---
  keywordResearch: (seed: string) =>
    request<KeywordResearchResponse>("/api/tools/keyword-research", {
      method: "POST",
      body: JSON.stringify({ seed }),
    }),
  trendingTopics: (refresh = false) => request<TrendingTopicsResponse>(`/api/tools/trending${refresh ? "?refresh=true" : ""}`),
  /** The account's last loaded set (shown again until a new load). */
  trendingLast: () => request<TrendingSnapshot>("/api/tools/trending/last"),
  /** Load a new set; it replaces the account's last load. */
  trendingLoad: (refresh = false, topic = "", geo = "US") => request<TrendingSnapshot>("/api/tools/trending/load", { method: "POST", body: JSON.stringify({ refresh, topic, geo }) }),
  savedTopics: () => request<SavedTopic[]>("/api/tools/trending/saved"),
  saveTopic: (item: TrendingTopicItem) =>
    request<SavedTopic>("/api/tools/trending/saved", {
      method: "POST",
      body: JSON.stringify({ topic: item.topic, source: item.source, views: item.views, score: item.score, comments: item.comments, subreddit: item.subreddit,
        link: item.link ?? null, industry: item.industry ?? null, detail: (item.news || item.traffic || item.detail || null)?.slice(0, 300) ?? null }),
    }),
  deleteSavedTopic: (id: string) => request<{ ok: boolean }>(`/api/tools/trending/saved/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // My websites & daily website watch (Phase 23)
  websites: () => request<WebsiteList>("/api/websites"),
  website: (id: string) => request<WebsiteOut>(`/api/websites/${encodeURIComponent(id)}`),
  addWebsite: (body: { url: string; label?: string; confirm_ownership: boolean; daily_email?: boolean; timezone?: string; report_hour?: number }) =>
    request<WebsiteOut & { job_id: string | null }>("/api/websites", { method: "POST", body: JSON.stringify(body) }),
  updateWebsite: (id: string, body: { label?: string; daily_email?: boolean; active?: boolean; timezone?: string; report_hour?: number }) =>
    request<WebsiteOut>(`/api/websites/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteWebsite: (id: string) => request<{ ok: boolean }>(`/api/websites/${encodeURIComponent(id)}`, { method: "DELETE" }),
  gscImportable: () => request<GscImportList>("/api/websites/import/gsc"),
  importFromGsc: (body: { properties: string[]; daily_email?: boolean; timezone?: string; report_hour?: number }) =>
    request<{ added: { id: string; host: string; url: string }[]; skipped: { property: string; reason: string }[] }>("/api/websites/import/gsc", { method: "POST", body: JSON.stringify(body) }),
  verifyWebsite: (id: string, method: "auto" | "gsc" | "meta" | "file" | "dns" = "auto") =>
    request<VerifyResult>(`/api/websites/${encodeURIComponent(id)}/verify`, { method: "POST", body: JSON.stringify({ method }) }),
  checkWebsiteNow: (id: string) => request<WebsiteOut>(`/api/websites/${encodeURIComponent(id)}/check`, { method: "POST" }),

  // Google indexing (Phase 24)
  indexingStatus: () => request<IndexingStatus>("/api/indexing/status"),
  indexingConnect: () => request<{ url: string }>("/api/indexing/connect"),
  indexingAvailable: () => request<{ connected: boolean; properties: { site_url: string; label: string; added: boolean }[] }>("/api/indexing/available"),
  indexingAddProperty: (site_url: string) => request<IndexProperty>("/api/indexing/properties", { method: "POST", body: JSON.stringify({ site_url }) }),
  indexingRemoveProperty: (id: string) => request<{ ok: boolean }>(`/api/indexing/properties/${encodeURIComponent(id)}`, { method: "DELETE" }),
  indexingProperty: (id: string) => request<IndexPropertyFull>(`/api/indexing/properties/${encodeURIComponent(id)}`),
  indexingRefresh: (id: string) => request<{ ok: boolean }>(`/api/indexing/properties/${encodeURIComponent(id)}/refresh`, { method: "POST" }),
  indexingInspect: (id: string, url: string) =>
    request<{ url: IndexUrlFull; remaining_today: number }>(`/api/indexing/properties/${encodeURIComponent(id)}/inspect`, { method: "POST", body: JSON.stringify({ url }) }),
  indexingUrls: (id: string, params: { bucket?: string; priority?: boolean; q?: string; state?: string; batch?: string; waiting?: boolean; page?: number; per_page?: number } = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && v !== "" && v !== false && q.set(k, String(v)));
    return request<{ items: IndexUrlItem[]; total: number; page: number; pages: number }>(`/api/indexing/properties/${encodeURIComponent(id)}/urls${q.toString() ? `?${q}` : ""}`);
  },
  indexingUrl: (urlId: string) => request<IndexUrlFull & { history: { at: string; bucket: string; state: string | null; source: string }[]; history_days: number }>(`/api/indexing/urls/${encodeURIComponent(urlId)}`),
  indexingSetPriority: (urlId: string, priority: boolean) =>
    request<IndexUrlItem>(`/api/indexing/urls/${encodeURIComponent(urlId)}`, { method: "PATCH", body: JSON.stringify({ priority }) }),
  indexingBulkPreview: (id: string, text: string) =>
    request<IndexBulkPreview>(`/api/indexing/properties/${encodeURIComponent(id)}/bulk/preview`, { method: "POST", body: JSON.stringify({ text }) }),
  indexingBulkStart: (id: string, text: string, priority: boolean) =>
    request<{ batch: IndexBatch }>(`/api/indexing/properties/${encodeURIComponent(id)}/bulk`, { method: "POST", body: JSON.stringify({ text, priority }) }),
  /** One bulk check's report: counts by status and Google's reasons across its pages. */
  indexingBatch: (id: string, batchId: string) =>
    request<IndexBatchReport>(`/api/indexing/properties/${encodeURIComponent(id)}/batches/${encodeURIComponent(batchId)}`),
  indexingBatches: (id: string) => request<{ items: IndexBatch[]; state: string }>(`/api/indexing/properties/${encodeURIComponent(id)}/batches`),
  indexingSitemaps: (id: string, live = false) =>
    request<{ items: IndexSitemap[]; can_write: boolean; can_submit?: boolean }>(`/api/indexing/properties/${encodeURIComponent(id)}/sitemaps${live ? "?live=true" : ""}`),
  indexingSubmitSitemap: (id: string, path: string) =>
    request<{ items: IndexSitemap[]; can_write: boolean; can_submit?: boolean }>(`/api/indexing/properties/${encodeURIComponent(id)}/sitemaps`, { method: "POST", body: JSON.stringify({ path }) }),
  indexingDeleteSitemap: (id: string, path: string) =>
    request<{ items: IndexSitemap[]; can_write: boolean; can_submit?: boolean }>(`/api/indexing/properties/${encodeURIComponent(id)}/sitemaps?path=${encodeURIComponent(path)}`, { method: "DELETE" }),
  indexingIndexNowVerify: (id: string) => request<{ ok: boolean; message: string }>(`/api/indexing/properties/${encodeURIComponent(id)}/indexnow/verify`, { method: "POST" }),
  indexingIndexNowOff: (id: string) => request<{ ok: boolean }>(`/api/indexing/properties/${encodeURIComponent(id)}/indexnow/off`, { method: "POST" }),
  indexingIndexNowSubmit: (id: string, urls: string[]) =>
    request<{ submitted: number; left_today: number | null }>(`/api/indexing/properties/${encodeURIComponent(id)}/indexnow`, { method: "POST", body: JSON.stringify({ urls }) }),
  indexingSettings: () => request<IndexSettingsOut>("/api/indexing/settings"),
  // Experimental Indexing API — job & livestream pages only
  indexingApiStatus: () => request<IndexingApiStatus>("/api/indexing/google-api"),
  indexingApiEnable: (terms_version: number) =>
    request<IndexingApiStatus>("/api/indexing/google-api/enable", { method: "POST", body: JSON.stringify({ accept: true, terms_version }) }),
  indexingApiDisable: () => request<IndexingApiStatus>("/api/indexing/google-api/disable", { method: "POST" }),
  indexingApiConnect: () => request<{ url: string }>("/api/indexing/google-api/connect"),
  indexingApiCheck: (id: string, url: string) =>
    request<IndexingApiCheck>(`/api/indexing/properties/${encodeURIComponent(id)}/google-api/check`, { method: "POST", body: JSON.stringify({ url }) }),
  indexingApiPublish: (id: string, urls: string[], kind: "URL_UPDATED" | "URL_DELETED") =>
    request<{ results: IndexingApiResult[]; sent: number; left_today: number }>(`/api/indexing/properties/${encodeURIComponent(id)}/google-api/publish`, {
      method: "POST", body: JSON.stringify({ urls, kind }),
    }),
  indexingSaveSettings: (body: { events?: Record<string, { app?: boolean; email?: boolean }>; health_threshold?: number; weekly_day?: number }) =>
    request<IndexSettingsOut>("/api/indexing/settings", { method: "PUT", body: JSON.stringify(body) }),
  indexingDownload: async (path: string, fallbackName: string) => {
    const res = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(body.detail ?? `Download failed: ${res.status}`);
    }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? fallbackName;
    a.click();
    URL.revokeObjectURL(url);
  },

  // Dashboard notifications (Phase 23)
  notifications: (params: { show?: "all" | "unread"; group?: "website" | "monitoring" | "indexing" | "other"; page?: number; per_page?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.show) q.set("show", params.show);
    if (params.group) q.set("group", params.group);
    if (params.page) q.set("page", String(params.page));
    if (params.per_page) q.set("per_page", String(params.per_page));
    return request<NotificationPage>(`/api/notifications${q.toString() ? `?${q}` : ""}`);
  },
  notificationSummary: () => request<{ unread: number; latest: NotificationItem[] }>("/api/notifications/summary"),
  markNotificationsRead: (body: { ids?: string[]; all?: boolean }) =>
    request<{ unread: number }>("/api/notifications/read", { method: "POST", body: JSON.stringify(body) }),
  deleteNotification: (id: string) => request<{ unread: number }>(`/api/notifications/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // --- Contact ---
  submitContact: (input: {
    name: string;
    email: string;
    company?: string;
    message: string;
    topic: "general" | "enterprise";
  }) =>
    request<{ message: string }>("/api/contact", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  // --- Deeper analysis of a check (features 9–21) ---
  checkInsights: (id: string) => request<CheckInsights>(`/api/check/${id}/insights`),
  analyzeCheck: (id: string, kind: "claims" | "intent" | "entities") =>
    request<Record<string, unknown>>(`/api/check/${id}/analyze/${kind}`, { method: "POST" }),
  checkVersions: (id: string) => request<{ document_id: string; versions: CheckVersion[] }>(`/api/check/${id}/versions`),
  checkContent: (id: string) =>
    request<{ check_id: string; version: number; content: string; inputs: Record<string, unknown>; source_url: string | null }>(`/api/check/${id}/content`),
  generateSchema: (input: { check_id?: string; types: string[]; fields: Record<string, string> }) =>
    request<{ blocks: Record<string, unknown>[]; validation: SchemaValidation[]; not_generated: string[] }>("/api/schema/generate", { method: "POST", body: JSON.stringify(input) }),
  priorities: () => request<{ items: PriorityItem[]; based_on: string; locked: boolean }>("/api/insights/priorities"),
  googleTop: (keyword: string, market = "us", own_url = "") =>
    request<{ keyword: string; results: { rank: number; url: string; domain: string; title: string }[]; own_rank: number | null }>(
      `/api/compare/google-top?keyword=${encodeURIComponent(keyword)}&market=${market}&own_url=${encodeURIComponent(own_url)}`),
  startComparison: (input: { own_url: string; competitor_urls: string[]; keyword: string }) =>
    request<ComparisonDetail>("/api/compare", { method: "POST", body: JSON.stringify(input) }),
  listComparisons: () => request<{ id: string; status: string; keyword: string; own_url: string; pages: number; created_at: string }[]>("/api/compare"),
  getComparison: (id: string) => request<ComparisonDetail>(`/api/compare/${id}`),

  // --- Whole-site scans (features 41–48) ---
  siteInfo: () => request<SiteInfo>("/api/site/info"),
  startSiteScan: (input: { url: string; max_pages?: number; confirm_ownership: boolean; render_js?: boolean }) =>
    request<SiteScanSummary>("/api/site/scans", { method: "POST", body: JSON.stringify(input) }),
  listSiteScans: () => request<SiteScanSummary[]>("/api/site/scans"),
  getSiteScan: (id: string) => request<SiteScanDetail>(`/api/site/scans/${id}`),
  siteScanPages: (id: string, params: { q?: string; only?: string; sort?: string; page?: number } = {}) => {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v && sp.set(k, String(v)));
    return request<{ total: number; page: number; pages: number; items: SiteScanPageRow[] }>(`/api/site/scans/${id}/pages?${sp.toString()}`);
  },
  cancelSiteScan: (id: string) => request<SiteScanSummary>(`/api/site/scans/${id}/cancel`, { method: "POST" }),
  deleteSiteScan: (id: string) => request(`/api/site/scans/${id}`, { method: "DELETE" }),
  downloadSiteScanCsv: async (id: string) => {
    const res = await fetch(`${API_BASE}/api/site/scans/${id}/export.csv`, { headers: authHeaders() });
    if (!res.ok) throw new Error((await res.json().catch(() => ({ detail: "Export failed" }))).detail);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? "site-scan.csv";
    a.click();
    URL.revokeObjectURL(url);
  },
  /** The whole audit report as a PDF (white-labelled when the package has branded reports). */
  downloadSiteScanPdf: async (id: string) => {
    const res = await fetch(`${API_BASE}/api/site/scans/${id}/report.pdf`, { headers: authHeaders() });
    if (!res.ok) throw new Error((await res.json().catch(() => ({ detail: "The PDF couldn't be made." }))).detail);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? "site-audit.pdf";
    a.click();
    URL.revokeObjectURL(url);
  },
  /** Sitelinks tab: the stored report, its history and the copyable markup. */
  sitelinks: (id: string) => request<SitelinksDetail>(`/api/site/scans/${id}/sitelinks`),
  /** Rankbist AI reasons, labels and titles for the Sitelinks report (one AI action; saved with the audit). */
  sitelinksExplain: (id: string, refresh = false) => request<{ ai: SitelinksAi; cached: boolean }>(`/api/site/scans/${id}/sitelinks/explain${refresh ? "?refresh=true" : ""}`, { method: "POST" }),
  downloadSitelinksGuide: async (id: string) => {
    const res = await fetch(`${API_BASE}/api/site/scans/${id}/sitelinks.docx`, { headers: authHeaders() });
    if (!res.ok) throw new Error((await res.json().catch(() => ({ detail: "The guide couldn't be made." }))).detail);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? "sitelinks-guide.docx";
    a.click();
    URL.revokeObjectURL(url);
  },
  siteScanShare: (id: string) => request<ShareState>(`/api/site/scans/${id}/share`),
  createSiteScanShare: (id: string) => request<ShareState>(`/api/site/scans/${id}/share`, { method: "POST" }),
  revokeSiteScanShare: (id: string) => request<ShareState>(`/api/site/scans/${id}/share`, { method: "DELETE" }),
  checkImageFile: async (file: File, opts: { focus?: string; page?: string; lang?: string } = {}) => {
    const token = getToken();
    const form = new FormData();
    form.append("file", file);
    form.append("focus", opts.focus ?? "");
    form.append("page", opts.page ?? "");
    form.append("lang", opts.lang ?? "en");
    const res = await fetch(`${API_BASE}/api/site/images/check`, { method: "POST", body: form, headers: authHeaders() });
    if (!res.ok) throw new Error((await res.json().catch(() => ({ detail: "Check failed" }))).detail);
    return (await res.json()) as ImageSeoResult;
  },

  // --- Keywords & search performance (features 52, 53, 56–61) ---
  keywordVolume: (keywords: string[]) =>
    request<{ keywords: { keyword: string; range: string | null; competition: string | null }[]; note: string }>("/api/tools/keyword-volume", { method: "POST", body: JSON.stringify({ keywords }) }),
  keywordClusters: (keywords: string[]) => request<KeywordClusters>("/api/tools/keyword-clusters", { method: "POST", body: JSON.stringify({ keywords }) }),
  monitoring: () => request<MonitoringOverview>("/api/monitoring"),
  addMonitoredUrl: (url: string, focus_keyword = "") => request<MonitoredUrl & { job_id?: string }>("/api/monitoring/urls", { method: "POST", body: JSON.stringify({ url, focus_keyword }) }),
  removeMonitoredUrl: (id: string) => request(`/api/monitoring/urls/${id}`, { method: "DELETE" }),
  runMonitoring: () => request<{ job_id: string }>("/api/monitoring/run", { method: "POST" }),
  updateMonitorSettings: (input: { email_alerts?: boolean; decay_threshold?: number }) => request("/api/monitoring/settings", { method: "PUT", body: JSON.stringify(input) }),
  markAlertsRead: () => request("/api/monitoring/alerts/read", { method: "POST" }),
  decayReport: () => request<DecayReport>("/api/monitoring/decay"),
  cannibalizationReport: () => request<CannibalizationReport>("/api/monitoring/cannibalization"),
  bingConnect: (api_key: string, site_url: string) => request("/api/bing/connect", { method: "POST", body: JSON.stringify({ api_key, site_url }) }),
  bingDisconnect: () => request("/api/bing", { method: "DELETE" }),
  bingReport: () => request<BingReport>("/api/bing/report"),
  /** `back: "analytics"` brings you back to the Analytics page after Google's screen (default: Page monitoring → AI traffic). */
  ga4ConnectUrl: (back?: "analytics") => request<{ url: string }>(`/api/ga4/connect${back ? `?back=${back}` : ""}`),
  // Website analytics (Phase 25). `source`: "ga4" (Google Analytics) or "tracker" (the Rankbist script, with `site`).
  analytics: () => request<AnalyticsStatus>("/api/analytics"),
  analyticsOverview: (q: AnalyticsQuery) => request<AnalyticsOverview>(`/api/analytics/overview?${aq(q)}`),
  analyticsRealtime: (q: AnalyticsQuery) => request<AnalyticsRealtime>(`/api/analytics/realtime?${aq(q)}`),
  analyticsPages: (q: AnalyticsQuery) => request<AnalyticsPages>(`/api/analytics/pages?${aq(q)}`),
  analyticsSeo: (q: AnalyticsQuery) => request<AnalyticsSeo>(`/api/analytics/seo?${aq(q)}`),
  analyticsConversions: (q: AnalyticsQuery) => request<AnalyticsConversions>(`/api/analytics/conversions?${aq(q)}`),
  analyticsAddNote: (day: string, text: string) => request<AnalyticsMarker>("/api/analytics/notes", { method: "POST", body: JSON.stringify({ day, text }) }),
  analyticsDeleteNote: (id: string) => request<{ deleted: boolean }>(`/api/analytics/notes/${id}`, { method: "DELETE" }),
  analyticsCampaigns: (q: AnalyticsQuery) => request<AnalyticsCampaigns>(`/api/analytics/campaigns?${aq(q)}`),
  analyticsSummary: (host = "") => request<AnalyticsSummary>(`/api/analytics/summary${host ? `?host=${encodeURIComponent(host)}` : ""}`),
  analyticsAddGoal: (g: { name: string; path: string; match: "starts" | "exact" }) => request<AnalyticsGoal>("/api/analytics/goals", { method: "POST", body: JSON.stringify(g) }),
  analyticsDeleteGoal: (id: string) => request<{ deleted: boolean }>(`/api/analytics/goals/${id}`, { method: "DELETE" }),
  analyticsShare: (source: AnalyticsSource, site = "") => request<ShareState>(`/api/analytics/share?source=${source}${site ? `&site=${site}` : ""}`),
  analyticsShareOn: (source: AnalyticsSource, site = "") => request<ShareState>(`/api/analytics/share?source=${source}${site ? `&site=${site}` : ""}`, { method: "POST" }),
  analyticsShareOff: (source: AnalyticsSource, site = "") => request<ShareState>(`/api/analytics/share?source=${source}${site ? `&site=${site}` : ""}`, { method: "DELETE" }),
  analyticsPdf: async (q: AnalyticsQuery) => {
    const res = await fetch(`${API_BASE}/api/analytics/report.pdf?${aq(q)}`, { headers: authHeaders() });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(typeof body.detail === "string" ? body.detail : `Download failed: ${res.status}`);
    }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? "analytics.pdf";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  },
  analyticsAddSite: (host: string) => request<TrackerSiteOut>("/api/analytics/sites", { method: "POST", body: JSON.stringify({ host }) }),
  analyticsDeleteSite: (id: string) => request<{ deleted: boolean }>(`/api/analytics/sites/${id}`, { method: "DELETE" }),
  analyticsCheckSite: (id: string) => request<{ found: boolean; visits_seen: boolean; message: string }>(`/api/analytics/sites/${id}/check`, { method: "POST" }),
  ga4Properties: () => request<{ id: string; name: string; account: string }[]>("/api/ga4/properties"),
  ga4SelectProperty: (property_id: string) => request("/api/ga4/property", { method: "POST", body: JSON.stringify({ property_id }) }),
  ga4Disconnect: () => request("/api/ga4", { method: "DELETE" }),
  ga4AiTraffic: () => request<Ga4AiTraffic>("/api/ga4/ai-traffic"),
  citations: () => request<CitationsData>("/api/citations"),
  saveCitations: (input: { domain: string; brand_names: string[]; prompts: string[]; active: boolean }) => request<CitationsData>("/api/citations", { method: "PUT", body: JSON.stringify(input) }),
  runCitations: () => request<{ job_id: string }>("/api/citations/run", { method: "POST" }),

  // --- Growth & integrations (features 62–67) ---
  publicCheck: (input: { content: string; title: string; focus_keyword: string }) =>
    request<PublicCheckResult>("/api/public/check", { method: "POST", body: JSON.stringify(input) }),
  shareStatus: (checkId: string) => request<ShareState>(`/api/check/${checkId}/share`),
  createShare: (checkId: string) => request<ShareState>(`/api/check/${checkId}/share`, { method: "POST" }),
  revokeShare: (checkId: string) => request<ShareState>(`/api/check/${checkId}/share`, { method: "DELETE" }),
  tokens: () =>
    request<{ tokens: AccessToken[]; api_url: string; clients: Record<"chrome" | "gdocs" | "wordpress", boolean>; published: Record<"chrome" | "gdocs" | "wordpress", boolean> }>("/api/tokens"),
  createToken: (name: string) => request<AccessToken & { token: string; note: string }>("/api/tokens", { method: "POST", body: JSON.stringify({ name }) }),
  revokeToken: (id: string) => request(`/api/tokens/${id}`, { method: "DELETE" }),
  fixQueue: (status = "") => request<{ fixes: FixProposal[]; counts: Record<string, number>; wordpress: boolean }>(`/api/agent/fixes${status ? `?status_filter=${status}` : ""}`),
  runFixAgent: () => request<{ job_id: string }>("/api/agent/run", { method: "POST" }),
  decideFix: (id: string, decision: "approve" | "reject" | "pending", after?: string) =>
    request<FixProposal>(`/api/agent/fixes/${id}/decide`, { method: "POST", body: JSON.stringify({ decision, after }) }),
  markFixApplied: (id: string) => request<FixProposal>(`/api/agent/fixes/${id}/applied`, { method: "POST", body: JSON.stringify({ via: "manual" }) }),

  // --- Phase 10: languages and white-label PDF reports ---
  languages: () => request<{ code: string; name: string; native_name: string; in_review: boolean }[]>("/api/languages"),
  branding: () => request<Branding>("/api/branding"),
  saveBranding: (input: Omit<Branding, "has_logo">) => request<Branding>("/api/branding", { method: "PUT", body: JSON.stringify(input) }),
  deleteBrandLogo: () => request<Branding>("/api/branding/logo", { method: "DELETE" }),
  uploadBrandLogo: async (file: File) => {
    const token = getToken();
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${API_BASE}/api/branding/logo`, { method: "POST", body: form, headers: authHeaders() });
    if (!res.ok) throw new Error((await res.json().catch(() => ({ detail: "Upload failed" }))).detail);
    return (await res.json()) as Branding;
  },
  downloadReportPdf: async (checkId: string) => {
    const res = await fetch(`${API_BASE}/api/check/${checkId}/report.pdf`, { headers: authHeaders() });
    if (!res.ok) throw new Error((await res.json().catch(() => ({ detail: "Download failed" }))).detail);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? "seo-report.pdf";
    a.click();
    URL.revokeObjectURL(url);
  },

  // --- Phase 10 part 2: workspaces, people, credit packs ---
  workspaces: () => request<WorkspacesData>("/api/workspaces"),
  createWorkspace: (name: string, site_url = "") => request<WorkspaceInfo>("/api/workspaces", { method: "POST", body: JSON.stringify({ name, site_url }) }),
  renameWorkspace: (id: string, name: string, site_url = "") => request<WorkspaceInfo>(`/api/workspaces/${id}`, { method: "PATCH", body: JSON.stringify({ name, site_url }) }),
  deleteWorkspace: (id: string) => request(`/api/workspaces/${id}`, { method: "DELETE" }),
  inviteMember: (workspaceId: string, email: string, role: MemberRole, permissions?: AreaLevels) =>
    request<WorkspaceMember & { email_status: string }>(`/api/workspaces/${workspaceId}/members`, { method: "POST", body: JSON.stringify({ email, role, permissions }) }),
  /** One invitation to several of your workspaces (one seat, one email). */
  inviteToWorkspaces: (workspaceIds: string[], email: string, role: MemberRole, permissions?: AreaLevels) =>
    request<MultiInviteResult>("/api/workspaces/invite", { method: "POST", body: JSON.stringify({ workspace_ids: workspaceIds, email, role, permissions }) }),
  changeMemberRole: (workspaceId: string, memberId: string, role: MemberRole, permissions?: AreaLevels) =>
    request<WorkspaceMember>(`/api/workspaces/${workspaceId}/members/${memberId}`, { method: "PATCH", body: JSON.stringify({ role, permissions }) }),
  removeMember: (workspaceId: string, memberId: string) => request(`/api/workspaces/${workspaceId}/members/${memberId}`, { method: "DELETE" }),
  acceptInvite: (token: string) => request<{ workspace_id: string; name: string; role: string }>("/api/workspaces/accept", { method: "POST", body: JSON.stringify({ token }) }),
  leaveWorkspace: (id: string) => request(`/api/workspaces/leave/${id}`, { method: "POST" }),
  credits: () => request<CreditsData>("/api/credits"),
  buyCreditPack: (pack_id: string) => request<{ purchase_id: string; transaction_id: string }>("/api/credits/purchase", { method: "POST", body: JSON.stringify({ pack_id }) }),
  confirmCreditPurchase: (transaction_id: string) => request<{ status: string; balances: Record<string, number> }>("/api/credits/confirm", { method: "POST", body: JSON.stringify({ transaction_id }) }),

  // --- Phase 12: files and guidance from a site scan ---
  siteFiles: (scanId: string) => request<SiteFiles>(`/api/site/scans/${scanId}/files`),
  siteGuidance: (scanId: string, params: { priority?: string; search?: string; offset?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.priority) q.set("priority", params.priority);
    if (params.search) q.set("search", params.search);
    if (params.offset) q.set("offset", String(params.offset));
    return request<SiteGuidance>(`/api/site/scans/${scanId}/guidance?${q}`);
  },
  siteDevGuide: (scanId: string, platform: string) => request<DevGuide>(`/api/site/scans/${scanId}/dev-guide?platform=${platform}`),
  markImplemented: (scanId: string, key: string, marked: boolean) =>
    request<{ implementation: ImplementationItem[] }>(`/api/site/scans/${scanId}/implementation`, { method: "POST", body: JSON.stringify({ key, marked }) }),
  verifySite: (scanId: string) => request<{ implementation: ImplementationItem[] }>(`/api/site/scans/${scanId}/verify`, { method: "POST" }),
  generateSiteMeta: (scanId: string) => request<{ job_id: string }>(`/api/site/scans/${scanId}/meta/generate`, { method: "POST" }),
  /** Downloads one generated file of a scan (sitemap.xml, robots.txt, schema.json, meta.csv, guidance.pdf). */
  downloadSiteFile: async (scanId: string, file: string) => {
    const token = getToken();
    const workspace = getWorkspace();
    const res = await fetch(`${API_BASE}/api/site/scans/${scanId}/${file}`, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(token && workspace ? { "X-Workspace": workspace } : {}) } });
    if (!res.ok) throw new Error((await res.json().catch(() => ({ detail: "Download failed" }))).detail);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? file;
    a.click();
    URL.revokeObjectURL(url);
  },

  // --- Phase 13: Local SEO & Google Business Profile ---
  localOverview: () => request<LocalOverview>("/api/local/overview"),
  saveBusiness: (input: BusinessDetails) => request<{ profile: BusinessDetails; missing: string[]; complete: boolean; recommended?: string[] }>("/api/local/profile", { method: "PUT", body: JSON.stringify(input) }),
  napCheck: () => request<NapCheck>("/api/local/nap"),
  writeGbpPack: () => request<GbpPack>("/api/local/pack", { method: "POST" }),
  localCitations: () => request<LocalCitations>("/api/local/citations"),
  setCitation: (key: string, status: "todo" | "listed" | "wrong") => request<LocalCitations>(`/api/local/citations/${key}`, { method: "POST", body: JSON.stringify({ status }) }),
  localTasks: () => request<LocalTasks>("/api/local/tasks"),
  localBusinesses: () => request<LocalBusinesses>("/api/local/businesses"),
  localGuide: (key: string) => request<LocalGuide>(`/api/local/tasks/${key}`),
  updateLocalTask: (key: string, input: { step?: number; done?: boolean; status?: "pending" | "in_progress" | "completed" }) =>
    request<LocalTasks>(`/api/local/tasks/${key}`, { method: "POST", body: JSON.stringify(input) }),
  localPhotos: () => request<LocalPhotos>("/api/local/photos"),
  deleteLocalPhoto: (id: string) => request(`/api/local/photos/${id}`, { method: "DELETE" }),
  uploadLocalPhoto: async (kind: string, file: File) => {
    const form = new FormData();
    form.append("kind", kind);
    form.append("file", file);
    const res = await fetch(`${API_BASE}/api/local/photos`, { method: "POST", body: form, headers: authHeaders() });
    if (!res.ok) throw new Error((await res.json().catch(() => ({ detail: "Upload failed" }))).detail);
    return res.json();
  },
  /** A private photo as an object URL the page can show. */
  localPhotoUrl: async (id: string) => {
    const res = await fetch(`${API_BASE}/api/local/photos/${id}`, { headers: authHeaders() });
    if (!res.ok) throw new Error("Photo not available");
    return URL.createObjectURL(await res.blob());
  },
  reviewKit: () => request<ReviewKit>("/api/local/reviews"),
  setReviewLink: (link: string) => request<ReviewKit>("/api/local/reviews/link", { method: "PUT", body: JSON.stringify({ link }) }),
  logReviewCount: (count: number) => request<ReviewKit>("/api/local/reviews/count", { method: "POST", body: JSON.stringify({ count }) }),
  downloadGbpPack: async () => {
    const res = await fetch(`${API_BASE}/api/local/pack.md`, { headers: authHeaders() });
    if (!res.ok) throw new Error((await res.json().catch(() => ({ detail: "Download failed" }))).detail);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = "google-business-profile-content.md";
    a.click();
    URL.revokeObjectURL(url);
  },

  // --- Article generator ---
  articleInfo: () => request<GeneratorInfo>("/api/articles/info"),
  listArticles: (params: { q?: string; status?: string; page?: number } = {}) => {
    const sp = new URLSearchParams();
    if (params.q) sp.set("q", params.q);
    if (params.status) sp.set("status_filter", params.status);
    if (params.page && params.page > 1) sp.set("page", String(params.page));
    const qs = sp.toString();
    return request<ArticlePage>(`/api/articles${qs ? `?${qs}` : ""}`);
  },
  createArticle: (input: { title: string; brief: string; min_words: number; options: Partial<ArticleOptions> }) =>
    request<ArticleDetail>("/api/articles", { method: "POST", body: JSON.stringify(input) }),
  getArticle: (id: string) => request<ArticleDetail>(`/api/articles/${id}`),
  updateArticlePlan: (id: string, input: { focus_keyword?: string; secondary_keywords?: string[]; article_format?: string; outline?: OutlineItem[] }) =>
    request<ArticleDetail>(`/api/articles/${id}/plan`, { method: "PUT", body: JSON.stringify(input) }),
  writeArticle: (id: string) => request<ArticleDetail>(`/api/articles/${id}/write`, { method: "POST" }),
  updateArticle: (id: string, input: Record<string, unknown>) =>
    request<ArticleDetail>(`/api/articles/${id}`, { method: "PUT", body: JSON.stringify(input) }),
  deleteArticle: (id: string) => request(`/api/articles/${id}`, { method: "DELETE" }),
  regenerateSection: (id: string, sectionId: string, mode: "rewrite" | "expand", instruction = "") =>
    request<{ job_id: string }>(`/api/articles/${id}/sections/${sectionId}/regenerate`, { method: "POST", body: JSON.stringify({ mode, instruction }) }),
  articleVersions: (id: string) => request<ArticleVersion[]>(`/api/articles/${id}/versions`),
  restoreArticleVersion: (id: string, number: number) =>
    request<ArticleDetail>(`/api/articles/${id}/versions/${number}/restore`, { method: "POST" }),
  getJob: (id: string) => request<JobStatus>(`/api/jobs/${id}`),
  /** Fetches an export with the account's token and returns its text or saves it as a file. */
  articleExport: async (id: string, ext: "docx" | "md" | "html", mode: "download" | "text"): Promise<string> => {
    const res = await fetch(`${API_BASE}/api/articles/${id}/export.${ext}`, { headers: authHeaders() });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(body.detail ?? `Request failed: ${res.status}`);
    }
    if (mode === "text") return res.text();
    const name = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? `article.${ext}`;
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
    return name;
  },

  // --- Newsletter (double opt-in) ---
  newsletterSubscribe: (email: string, consent: boolean, source = "website") =>
    request<{ message: string }>("/api/newsletter/subscribe", {
      method: "POST",
      body: JSON.stringify({ email, consent, source }),
    }),
  newsletterConfirm: (token: string) =>
    request<{ message: string }>("/api/newsletter/confirm", { method: "POST", body: JSON.stringify({ token }) }),
  myNewsletter: () => request<{ subscribed: boolean; consent_text: string }>("/api/newsletter/me"),
  setMyNewsletter: (subscribed: boolean) =>
    request<{ subscribed: boolean }>("/api/newsletter/me", { method: "PUT", body: JSON.stringify({ subscribed }) }),
  newsletterUnsubscribe: (token: string) =>
    request<{ message: string }>("/api/newsletter/unsubscribe", { method: "POST", body: JSON.stringify({ token }) }),

  // --- Blog ---
  listPosts: () => request<PostSummary[]>("/api/blog/posts"),
  getPost: (slug: string) => request<PostDetail>(`/api/blog/posts/${slug}`),

  // --- Referral credits (customer's own invite + wallet) ---
  referralSummary: () =>
    request<{
      code: string; link: string; points: number; referred: number; credited: number;
      points_per_referral: number; program_open: boolean;
      point_cost: Record<string, number>; kind_label: Record<string, string>;
      history: { delta: number; reason: string; detail: string; at: string }[];
    }>("/api/referral"),
  referralSpend: (kind: string, units: number) =>
    request<{ points: number; kind: string; added: number; credit_balance: number; message: string }>(
      "/api/referral/spend", { method: "POST", body: JSON.stringify({ kind, units }) }),

  // --- Admin "log in as user" support session (never used by ordinary customers) ---
  impersonateExchange: (code: string) =>
    request<{ token: string; mode: "view" | "edit"; expires_in_minutes: number; admin_name: string;
              user: { id: string; email: string; name: string | null } }>(
      "/api/auth/impersonate/exchange", { method: "POST", body: JSON.stringify({ code }) }),
  impersonateEnd: () => request<{ ok: boolean }>("/api/auth/impersonate/end", { method: "POST" }),
};

export interface PostSummary {
  slug: string;
  title: string;
  excerpt: string;
  author_name: string;
  published_at: string | null;
  thumbnail_url?: string | null;
  image_alt?: string | null;
  reading_minutes?: number;
  updated_at?: string | null;
}

export interface PostDetail extends PostSummary {
  body_markdown: string;
  meta_title?: string | null;
  meta_description?: string | null;
  focus_keyword?: string | null;
  secondary_keywords?: string[];
  faq?: { q: string; a: string }[];
  og_image_url?: string | null;
  headings?: { id: string; text: string }[];
  json_ld?: Record<string, unknown>[];
  related?: PostSummary[];
}

export interface PostPage {
  items: PostSummary[];
  total: number;
  page: number;
  pages: number;
  per_page: number;
  query: string;
}

export interface KeywordResearchResponse {
  seed: string;
  suggestions: string[];
  questions: string[];
  trend_direction: "rising" | "steady" | "falling" | null;
  trend_series: number[] | null;
  note: string;
}

export interface TrendingTopicItem {
  topic: string;
  source: string;
  score: number | null;
  comments: number | null;
  views: number | null;
  subreddit: string | null;
  /** Wikipedia: the day the list is from. */
  day?: string | null;
  /** Google Trends: Google's rough search count ("200+"), and a news story about it. */
  traffic?: string | null;
  news?: string | null;
  link?: string | null;
  /** Which industry it belongs to; "source" = the source said so, "ai"/"keywords" = worked out. */
  industry?: string | null;
  industry_by?: "source" | "ai" | "keywords" | null;
  detail?: string | null;
  published?: string | null;
}

export interface TrendingTopicsResponse {
  /** What the set is about; null = everything trending. */
  topic?: string | null;
  /** Google Trends "Trending now" (older saved loads don't have it). */
  google?: TrendingTopicItem[];
  /** Reddit is only used with keys Reddit approved. */
  reddit_enabled?: boolean;
  wikipedia: TrendingTopicItem[];
  hackernews: TrendingTopicItem[];
  reddit: TrendingTopicItem[];
  news?: TrendingTopicItem[];
  bluesky?: TrendingTopicItem[];
  mastodon?: TrendingTopicItem[];
  youtube?: TrendingTopicItem[];
  youtube_enabled?: boolean;
  geo?: string;
  errors: string[];
  fetched_at?: string | null;
  cached?: boolean;
}

export interface TrendingSnapshot {
  data: TrendingTopicsResponse | null;
  loaded_at: string | null;
}

export interface WebsiteTest {
  key: string;
  label: string;
  /** How a failing test is worded as something to do (empty when it passes). */
  todo?: string;
  ok: boolean;
  severity: "high" | "medium" | "low";
  detail: string;
  fix: string;
  where: string;
}

export interface WebsiteTask {
  kind: "audit" | "audit_issue" | "test";
  title: string;
  detail: string;
  link?: string;
  where?: string;
  level?: string;
}

export interface WebsiteCheckOut {
  id: string;
  status: "ok" | "problems" | "down";
  score: number;
  created_at: string;
  problems: number;
  high: number;
  changes: { kind: string; text: string; key?: string; severity?: string }[];
  tasks: WebsiteTask[];
  meta: WebsiteCheckMeta;
  results?: WebsiteTest[];
  /** "daily" (end-of-day report), "first" (just added) or "manual" (Check now). */
  run?: "daily" | "first" | "manual";
  /** false while tonight's full audit is still being folded into the report. */
  final?: boolean;
}

export interface WebsiteAuditSummary {
  state: "pending" | "done" | "failed" | "timeout" | "skipped";
  scan_id?: string;
  reason?: string | null;
  pages_cap?: number;
  health_score?: number | null;
  pages?: number;
  errors?: number;
  warnings?: number;
  notices?: number;
  compared?: boolean;
  previous_health?: number | null;
  fixed?: number;
  new?: number;
  top_issues?: { label: string; level: "error" | "warning" | "notice"; pages: number; fix: string }[];
}

export interface WebsiteCheckMeta {
  status_code?: number;
  response_ms?: number;
  final_url?: string;
  certificate_days?: number | null;
  sitemap_urls?: number;
  title?: string;
  error?: string | null;
  audit?: WebsiteAuditSummary;
  speed?: { state: "done" | "failed" | "skipped"; reason?: string; score?: number; lab?: Record<string, string>; opportunities?: { title: string; savings_ms: number }[]; field?: string | null };
  authority?: { domain: string; score: number; referring_domains: number; release: string };
  search?: { site_url: string; clicks: number; clicks_before: number; impressions: number; position: number | null; position_before: number | null; period_end?: string | null };
}

export interface WebsiteOut {
  id: string;
  url: string;
  host: string;
  label: string;
  daily_email: boolean;
  active: boolean;
  last_checked_at: string | null;
  last_status: "ok" | "problems" | "down" | null;
  last_score: number | null;
  created_at: string;
  latest: WebsiteCheckOut | null;
  history: { date: string; score: number; status: string }[];
  audit: { scan_id: string; status: string; average_score: number | null; pages: number; created_at: string } | null;
  schedule: { frequency: string; active: boolean; next_run_at: string | null } | null;
  /** The site's time zone and the hour (0–23) its end-of-day report runs. */
  timezone: string;
  report_hour: number;
  next_report_at: string;
  report_running: boolean;
  /** Google index status of the matching Indexing property (Phase 24), if any. */
  indexing?: { property_id: string; label: string; health: number | null; indexed: number; not_indexed: number; errors: number; checked: number } | null;
  /** The last seven days: score change and problems fixed (website page only). */
  week?: { days: number; checks: number; score: number; score_before: number; score_change: number; fixed: number; new: number; health: number | null; health_before: number | null } | null;
  state: "running" | "ready";
  /** Ownership: nothing runs on a website until it is verified. */
  verified: boolean;
  verify_method: "gsc" | "meta" | "file" | "dns" | "legacy" | null;
  verify_failures: number;
  verified_at: string | null;
  verification: WebsiteVerification | null;
}

export interface WebsiteVerification {
  token: string;
  meta: { tag: string; where: string };
  file: { name: string; url: string; content: string };
  dns: { type: "TXT"; host: string; value: string };
}

export interface GscImportList {
  connected: boolean;
  properties: { property: string; url: string; host: string; added: boolean }[];
  limit: number;
  used: number;
}

export interface VerifyResult {
  verified: boolean;
  method: string | null;
  results: { method: "gsc" | "meta" | "file" | "dns"; ok: boolean; detail: string }[];
  job_id: string | null;
  website: WebsiteOut;
}

export interface WebsiteList {
  available: boolean;
  limit: number;
  used: number;
  includes?: { audit_pages: number; speed: boolean; authority: boolean };
  websites: WebsiteOut[];
}

export interface NotificationItem {
  id: string;
  kind: string;
  group: "website" | "monitoring" | "indexing" | "other";
  title: string;
  body: string;
  url: string | null;
  link: string;
  level: "info" | "warning" | "critical";
  read: boolean;
  created_at: string;
}

export interface NotificationPage {
  items: NotificationItem[];
  total: number;
  page: number;
  pages: number;
  unread: number;
}

export interface SavedTopic extends TrendingTopicItem {
  id: string;
  saved_at: string;
}

export interface PlanOut {
  plan_name: string;
  description: string;
  is_featured: boolean;
  cta_label: string;
  price_display_override: string | null;
  billing_caption: string | null;
  display_order: number;
  bullets: string[];
  price_version_id: string | null;
  price_cents: number | null;
  currency: string | null;
  billing_interval: string | null;
  auto_fix_enabled: boolean;
  full_issue_detail_enabled: boolean;
  weekly_url_check_limit: number;
  site_scans_per_month: number;
  image_checks_enabled: boolean;
  paddle_price_id: string | null;
  /** The launch offer on the default price (see PlanPromo). */
  promo?: PlanPromo | null;
  /** Every price on sale for this plan, one per interval (newer API). */
  prices?: PlanPrice[];
  slug?: string | null;
  /** The "Start for free" package: no checkout. */
  is_free?: boolean;
  /** Live features this package includes. */
  features?: PlanFeature[];
  /** How many of those the bullets don't already list. */
  more_features_count?: number;
  /** -1 = unlimited, 0 = not included. */
  limits?: Record<string, number>;
}

export interface PlanFeature {
  key: string;
  name: string;
  description: string;
  group: string;
  uses_ai: boolean;
}

export interface PlanPrice {
  price_version_id: string;
  price_cents: number;
  currency: string;
  billing_interval: "WEEKLY" | "MONTHLY" | "YEARLY" | string;
  paddle_price_id: string | null;
  promo?: PlanPromo | null;
}

/** The launch offer on one price (admin → Discount offer). Shown only between starts_at and ends_at (lib/promo.ts);
 *  checkout applies Paddle's discount by itself then. */
export interface PlanPromo {
  label: string;
  percent: number;
  /** The discounted amount in the price's own currency. */
  price_cents: number;
  interval: string;
  /** Monthly: how many monthly payments are discounted (the first counts). Yearly: 1, the first year. */
  periods: number;
  discount_id: string;
  starts_at: string;
  ends_at: string;
}

/** /api/billing/promo: the discount offer for the top bar and the popup (admin → Discount offer). */
export type LaunchOffer =
  | { active: false }
  | {
      active: true;
      /** Changes with every new offer, so a closed popup shows again for the next one. */
      id: string;
      label: string;
      bar_text: string;
      popup_title: string;
      popup_text: string;
      popup_button: string;
      show_popup: boolean;
      link: string | null;
      starts_at: string;
      ends_at: string;
      max_percent: number;
      min_percent: number;
      big_percent: number;
      other_percent: number;
      monthly_periods: number;
      first_purchase_only: boolean;
      /** One code per discount size (monthly plans), biggest first. */
      codes: { percent: number; plans: string; code: string }[];
    };

export interface CheckoutResponse {
  transaction_id: string;
  paddle_client_token_data: Record<string, unknown>;
  /** False when this buyer can't use the running offer (it's for first plan purchases): no "Add discount" field. */
  allow_discount_codes?: boolean;
}

export interface SubscriptionOut {
  plan_name: string;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  is_trialing?: boolean;
}

export interface UsageItem {
  key: "check" | "url" | "site_scan" | string;
  label: string;
  used: number;
  limit: number;
  period: "day" | "week" | "month" | string;
  resets_at: string;
}

export interface AiFix {
  key: string;
  label: string;
  category: string;
  current: string;
  suggested: string;
  why: string;
  /** Where the fix goes ("Replace the opening paragraph"); missing on fixes written before it existed. */
  where?: string;
}

export interface CheckHistoryItem {
  id: string;
  overall_score: number;
  word_count: number;
  mode: string;
  created_at: string;
  title?: string | null;
  source_url?: string | null;
  issue_count?: number;
  /** Only from getCheckHistoryDetailed: per category, points earned / possible and failed checks. */
  categories?: Record<string, { earned: number; max: number; issues: number }> | null;
}

export interface CheckAnalytics {
  trend: { id: string; created_at: string; overall_score: number; issue_count: number }[];
  category_issue_totals: Record<string, number>;
  category_avg_scores: Record<string, number>;
  mode_counts: Record<string, number>;
  checks_last_7_days: number;
  checks_last_30_days: number;
  best_score: number;
  latest_score: number | null;
}

export interface DashboardStats {
  total_checks: number;
  total_checks_prev_period: number;
  avg_score: number;
  avg_score_prev_period: number;
  total_words: number;
  total_words_prev_period: number;
  total_issues_found: number;
  total_issues_prev_period: number;
}

export interface GscQuery {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface GscRankings {
  start_date: string;
  end_date: string;
  queries: GscQuery[];
  /** Closest-to-the-top queries; null when the package doesn't include them. */
  opportunities?: GscQuery[] | null;
}

// --- Article generator ---

export interface GeneratorInfo {
  available: boolean;
  features: Record<string, boolean>;
  max_words: number;
  min_words: number;
  articles_per_month: number;
  formats: Record<string, string>;
  tones: Record<string, string>;
  languages: Record<string, string>;
  target_score: number;
}

export interface ArticleOptions {
  focus_keyword: string;
  audience: string;
  tone: string;
  language: string;
  website_url: string;
  include_faq: boolean;
  auto_write: boolean;
}

export interface OutlineItem {
  id?: string;
  heading: string;
  points: string[];
  target_words: number;
  table?: boolean;
}

export interface ArticleSection {
  id: string;
  heading: string;
  markdown: string;
}

export type ArticleStatus = "planning" | "outline_ready" | "writing" | "draft" | "final" | "failed";

export interface ArticleSummary {
  id: string;
  title: string;
  status: ArticleStatus;
  focus_keyword: string;
  article_format: string;
  seo_score: number | null;
  word_count: number;
  min_words: number;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface ArticlePage {
  items: ArticleSummary[];
  total: number;
  page: number;
  pages: number;
}

export interface ArticleScoreReport {
  score: number;
  target: number;
  reached_target: boolean;
  word_count: number;
  issue_count: number;
  category_scores: Record<string, number>;
  issues: { key: string; category: string; label: string; tip: string; value: string | null; points_lost: number }[];
}

export interface InternalLink {
  kind: "page" | "topic";
  url: string | null;
  topic?: string;
  anchor: string;
  section_id: string | null;
  placement: string;
  reason: string;
}

export interface ArticleDetail extends ArticleSummary {
  brief: string;
  options: Partial<ArticleOptions>;
  error: string | null;
  job_id: string | null;
  stage_message: string | null;
  research: { suggestions?: string[]; questions?: string[]; trend?: string | null; faq_source?: string | null };
  secondary_keywords: string[];
  intent: string;
  outline: OutlineItem[];
  intro: string;
  sections: ArticleSection[];
  body_markdown: string;
  meta_title: string;
  meta_description: string;
  slug: string;
  excerpt: string;
  faq: { q: string; a: string }[];
  internal_links: InternalLink[];
  json_ld: Record<string, unknown>[];
  json_ld_problems: string[];
  score_report: ArticleScoreReport | null;
  score_history: number[];
  serp_preview: { label: string; host: string; path: string; desktop: { title: string; description: string }; mobile: { title: string; description: string } } | null;
  placeholders: string[];
  /** What to review before marking final; null when the server has the review switched off. */
  claim_review: ClaimReview | null;
  target_score: number;
}

export type ClaimKind = "placeholder" | "statistic" | "attribution" | "dated";

export interface ClaimReview {
  count: number;
  counts: Record<ClaimKind, number>;
  items: { kind: ClaimKind; text: string; match: string | null; why: string; fix: string }[];
}

export interface ArticleVersion {
  number: number;
  reason: string;
  seo_score: number | null;
  word_count: number;
  created_at: string;
  current: boolean;
}

export interface JobStatus {
  id: string;
  kind: string;
  status: "queued" | "running" | "succeeded" | "failed" | "canceled";
  progress_current: number;
  progress_total: number;
  progress_message: string | null;
  error: string | null;
}

// --- Deeper analysis of a check ---

export interface Highlight {
  type: string;
  category: string;
  label: string | null;
  start: number;
  end: number;
  text: string | null;
  explanation: string | null;
  fix: string | null;
}

export interface PriorityItem {
  key: string;
  label: string;
  category: string;
  tip: string;
  value: string | null;
  points: number;
  severity: "high" | "medium" | "low";
  priority: number;
  check_id?: string;
  page?: string;
  source_url?: string | null;
  impressions?: number | null;
}

export interface SchemaValidation {
  type: string;
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export interface CheckVersion {
  check_id: string;
  version: number;
  score: number;
  word_count: number;
  issue_count: number;
  created_at: string;
  current: boolean;
  has_text: boolean;
  delta: number | null;
  fixed: string[];
  new_issues: string[];
  fixed_count?: number;
  new_count?: number;
}

export interface ClaimItem {
  type: string;
  label: string;
  sentence: string;
  start: number;
  end: number;
  trigger: string | null;
  advice: string;
  source: string;
}

export interface CheckInsights {
  check_id: string;
  has_text: boolean;
  features: Record<string, boolean>;
  ai_available: boolean;
  mode: string;
  source_url: string | null;
  title: string;
  focus_keyword: string;
  version_number: number;
  content: string | null;
  highlights: Highlight[] | null;
  highlights_locked: boolean;
  rhythm: { paragraphs: { start: number; end: number; sentences: number; variation: number | null; level: "varied" | "somewhat_flat" | "flat" | "short"; note: string }[]; overall_variation: number | null; flat_paragraphs: number } | null;
  ai_answer: { score: number; checklist: { key: string; label: string; passed: boolean; found: string; fix: string | null }[] } | null;
  trust: { found: number; total: number; signals: { key: string; label: string; found: boolean; evidence: string | null; how_to_add: string | null }[] } | null;
  structured_data: { found: Record<string, unknown>[]; found_validation: SchemaValidation[]; invalid_blocks: number; can_detect: boolean; generatable: string[] } | null;
  priority: { items: PriorityItem[]; impressions: number | null; based_on: string } | null;
  analyses: {
    claims?: { claims: ClaimItem[]; total: number; used_ai: boolean; note: string };
    intent?: { keyword: string; intent: string; intent_description: string; expected_formats: string[]; detected_format: string; fits: boolean; warning: string | null; used_ai: boolean; reason?: string | null };
    entities?: { covered: number; total: number; percent: number; used_ai: boolean; used_search: boolean; subtopics: { topic: string; source: string; covered: boolean; where: string | null; add_to: string | null }[] };
  };
}

export interface ComparedPage {
  url: string;
  name: string;
  ok: boolean;
  error: string | null;
  title?: string;
  score?: number;
  word_count?: number;
  issue_count?: number;
  heading_count?: number;
  trust_found?: number;
  trust_total?: number;
  ai_answer_score?: number;
  has_structured_data?: boolean;
  category_scores?: Record<string, number>;
}

export interface ComparisonDetail {
  id: string;
  status: "running" | "done" | "failed";
  keyword: string;
  own_url: string;
  competitor_urls: string[];
  job_id: string | null;
  error: string | null;
  created_at: string;
  features: { difficulty: boolean; info_gain: boolean };
  result: {
    pages: ComparedPage[];
    difficulty: { score: number; label: string; note: string; pages_used: number; factors: { key: string; label: string; value: number; weight: number; detail: string }[] } | null;
    information_gain: { skipped?: string; unique_to_you?: string[]; missing_from_you?: { topic: string; covered_by: string[]; all_competitors: boolean }[]; shared?: string[]; used_ai?: boolean; note?: string } | null;
  } | null;
}

// --- Whole-site scans ---

export interface SiteInfo {
  available: boolean;
  max_pages: number;
  scans_per_month: number;
  features: Record<string, boolean>;
  renderer_available: boolean;
  image_upload: boolean;
}

export interface SiteScanSummary {
  id: string;
  root_url: string;
  host: string;
  status: "queued" | "running" | "done" | "failed" | "canceled";
  job_id: string | null;
  max_pages: number;
  pages_scanned: number;
  /** Average CONTENT score of the pages read (how well each page's text is optimised, 0–100). */
  average_score: number | null;
  error: string | null;
  created_at: string;
  finished_at: string | null;
  /** The Health Score the report leads with: share of pages read with no technical error. */
  health?: { health_score: number | null; pages: number; pages_with_errors: number; errors: number; warnings: number; notices: number } | null;
}

export interface ImageFileCheck {
  name: string;
  url?: string;
  page?: string;
  format: string | null;
  bytes: number;
  width: number | null;
  height: number | null;
  alt: string | null;
  issues: { key: string; label: string; fix: string; why?: string }[];
  ok: boolean;
}

export interface ImageSeoWords {
  description?: string;
  alt?: string;
  alt_short?: string;
  alt_keyword?: string;
  file_name?: string;
  title?: string;
  caption?: string;
  long_description?: string;
  keywords?: string[];
  text_in_image?: string;
  decorative?: boolean;
  kind?: string;
}

/** The Image SEO helper's answer for one uploaded file. */
export interface ImageSeoResult extends ImageFileCheck {
  score: number;
  load: { fast_s: number; slow_s: number };
  meta: { camera: string | null; taken: string | null; gps: boolean; exif_kb: number; mode: string | null; animated: boolean; transparent: boolean };
  words: ImageSeoWords;
  /** ai: written from the picture · ai_text: from the details, without seeing it · basic: from the keyword / file name · none */
  words_source: "ai" | "ai_text" | "basic" | "none";
  language: string;
  note: string;
}

export interface SiteScanDetail extends SiteScanSummary {
  options: { render_js?: boolean };
  features: Record<string, boolean>;
  summary: {
    pages_read: number;
    hit_page_cap: boolean;
    max_pages: number;
    sitemap_urls: number;
    robots_found: boolean;
    blocked_by_robots: string[];
    crawl_delay: number;
    patterns?: { pages: number; scored_pages: number; items: { key: string; count: number; percent: number; text: string }[]; score_bands: Record<string, number>; top_failing_tests: { label: string; count: number; percent: number }[]; status_codes: Record<string, number> };
    technical?: { issue_counts: Record<string, number>; total_issues: number; pages_with_issues: number; broken_links: { internal: { from: string; to: string; status: number; anchor: string }[]; external: { from: string; to: string; status: number }[]; external_checked: number; external_found: number }; sitemap_blocked?: string[] };
    duplicates?: { near_duplicates: { a: string; b: string; similarity: number }[]; near_duplicate_pages: number; templated_pages: { url: string; templated_percent: number }[]; thin_pages: { url: string; words: number }[]; thin_count: number; average_boilerplate_percent: number | null; risk: "low" | "medium" | "high"; risk_note: string; pages_compared: number };
    links?: { pages: number; links: number; orphans: string[]; orphan_count: number; needing_links: string[]; suggestions: { from: string; to: string; anchor: string; reason: string }[]; most_linked: { url: string; inbound: number }[] };
    ai_crawlers?: { robots_found: boolean; crawlers: { agent: string; company: string; purpose: string; allowed: boolean; rule: string }[]; llms_txt: boolean; llms_note: string; noindex_pages: number; nosnippet_pages: number; max_snippet_pages: number };
    js?: { available: boolean; note: string; pages_checked?: number; pages_failed?: number; depends_on_js?: { url: string; raw_words: number; rendered_words: number; js_only_percent: number }[] };
    images?: { images_found: number; unique_images: number; files_checked: number; missing_alt: { page: string; image: string }[]; missing_alt_count: number; files: ImageFileCheck[]; issue_counts: Record<string, number> };
    /** Google Lighthouse on the start page, mobile and desktop, run with the scan. */
    lighthouse?: import("@/components/lighthouse/LighthouseReport").ScanLighthouse;
    /** Sitelinks & structure (feature 132), measured from this crawl. */
    sitelinks?: SitelinksReport;
  } | null;
}

export interface SitelinksFactor { key: string; label: string; weight: number; value: number; points: number; facts: string }
export interface SitelinksCandidate {
  url: string; title: string; h1: string; strength: number; reasons: string[]; click_depth: number | null; inlinks: number;
  in_nav: boolean; in_footer: boolean; from_home: boolean; title_unique: boolean; breadcrumbs: boolean; suggested_anchor: string; words: number;
}
export interface SitelinksAction {
  key: string; title: string; problem: string; why: string; steps: string[]; where: Partial<Record<"wordpress" | "shopify" | "custom", string>>;
  asset: { kind: string; label: string; text?: string; ref?: "website" | "breadcrumbs" } | null; pages: string[]; impact: number; priority: "high" | "medium" | "low";
}
export interface SitelinksAi { summary: string; pages: Record<string, { why: string; anchor: string; title: string }>; actions: Record<string, string> }
export interface SitelinksReport {
  available: boolean; note: string; score?: number; band?: string; summary?: string; factors?: SitelinksFactor[]; candidates?: SitelinksCandidate[];
  actions?: SitelinksAction[]; top_actions?: string[]; pages_measured?: number;
  navigation?: { found: boolean; items: { url: string; anchor: string; status: number | null; crawled: boolean }[]; count: number; footer_count: number; missing_key_pages: string[]; broken: string[]; too_many: boolean };
  home?: { url: string; website_schema: boolean; links: number };
  ai?: SitelinksAi;
}
export interface SitelinksDetail {
  report: SitelinksReport; history: { scan_id: string; at: string; score: number }[]; previous_score: number | null; delta: number | null;
  assets: { website_tags?: string; breadcrumbs?: Record<string, string>; breadcrumb_example?: string; files_href?: string }; ai: SitelinksAi | null; fix_queue: boolean;
}

export interface SiteScanPageRow {
  url: string;
  status: number;
  score: number | null;
  words: number;
  title: string;
  depth: number;
  in_sitemap: boolean;
  inbound_links: number | null;
  issue_count: number | null;
  issues: { key: string; label: string; severity: string; detail: string }[];
  failed_tests: string[];
  templated_percent: number | null;
  noindex: boolean;
  error: string | null;
}

// --- Keywords & search performance ---

export interface KeywordClusters {
  method: "meaning" | "shared words";
  keywords: number;
  ungrouped: string[];
  clusters: { head: string; keywords: string[]; size: number; pillar: { title: string; covers: string[]; source: string } }[];
}

export interface MonitoredUrl {
  id: string;
  url: string;
  focus_keyword: string;
  last_score: number | null;
  previous_score: number | null;
  delta: number | null;
  last_checked_at: string | null;
  last_check_id: string | null;
  last_error: string | null;
}

export interface MonitoringAlert {
  id: string;
  kind: "decay" | "score_drop" | "new_issues" | "monitor_error";
  title: string;
  body: string;
  url: string | null;
  check_id: string | null;
  created_at: string;
  read: boolean;
}

export interface MonitoringOverview {
  features: Record<"monitoring" | "decay" | "cannibalization" | "bing" | "ga4" | "citations", boolean>;
  limits: { monitored_urls: number; citation_prompts: number };
  settings: { email_alerts: boolean; decay_threshold: number };
  urls: MonitoredUrl[];
  alerts: MonitoringAlert[];
  unread_alerts: number;
  connections: {
    search_console: { connected: boolean; site_url: string | null };
    bing: { connected: boolean; site_url: string | null };
    ga4: { connected: boolean; property_id: string | null; property_name: string | null; needs_reconnect?: boolean };
  };
}

export interface DecayReport {
  connected: boolean;
  threshold?: number;
  current_period?: [string, string];
  previous_period?: [string, string];
  pages: { page: string; impressions_before: number; impressions_now: number; impressions_drop: number; clicks_before: number; clicks_now: number; clicks_drop: number | null }[];
}

export interface CannibalizationReport {
  connected: boolean;
  queries: { query: string; impressions: number; clicks: number; keep: string; suggestion: string; pages: { page: string; clicks: number; impressions: number; position: number; share: number }[] }[];
}

export interface BingReport {
  site_url: string;
  clicks: number;
  impressions: number;
  queries: { query: string; clicks: number; impressions: number; position: number }[];
  traffic: { date: string; clicks: number; impressions: number }[];
  backlinks: { total: number; note: string; pages: { url: string; links: number }[] };
}

/* ---------- Website analytics (Phase 25) ---------- */

export type AnalyticsSource = "ga4" | "tracker";
export type AnalyticsCompare = "previous" | "year";
/** `days` (7/28/90), or a custom `start`–`end` (YYYY-MM-DD); compared with the period before or last year. */
export interface AnalyticsQuery { days: number; source: AnalyticsSource; site?: string; start?: string; end?: string; compare?: AnalyticsCompare }
const aq = (q: AnalyticsQuery) =>
  `days=${q.days}&source=${q.source}${q.site ? `&site=${encodeURIComponent(q.site)}` : ""}${q.start && q.end ? `&start=${q.start}&end=${q.end}` : ""}${q.compare === "year" ? "&compare=year" : ""}`;
export interface AnalyticsPeriod { start: string; end: string; previous_start: string; previous_end: string; compare: AnalyticsCompare; days: number }
export interface AnalyticsGoal { id: string; name: string; path: string; match: "starts" | "exact" }
export interface ShareState { shared: boolean; url: string | null; views: number; created_at?: string }
export interface AnalyticsCampaigns {
  period: AnalyticsPeriod; days: number; source: AnalyticsSource; note: string;
  rows: { name: string; source_medium: string; sessions: number; users: number; conversions: number; rate: number; previous_sessions: number }[];
}
export interface AnalyticsSummary {
  source: AnalyticsSource | null; host?: string; period?: AnalyticsPeriod; error?: string;
  totals?: AnalyticsTotals; change?: Partial<Record<keyof AnalyticsTotals, number | null>>; trend?: number[]; ai_sessions?: number;
}

export interface TrackerSiteOut {
  id: string; host: string; key: string; snippet: string; created_at: string;
  first_seen_at: string | null; last_seen_at: string | null; capped: boolean;
}

export interface AnalyticsStatus {
  features: { overview: boolean; pages: boolean; conversions: boolean; annotations: boolean; tracker: boolean; ai_traffic: boolean; share: boolean; pdf_branding: boolean };
  ga4: { connected: boolean; property_id: string | null; property_name: string | null; needs_reconnect: boolean };
  gsc: { connected: boolean; site_url: string | null };
  tracker: { sites: TrackerSiteOut[]; limit_sites: number; used_month: number; limit_month: number };
  default_source: AnalyticsSource | null;
  host: string;
  periods: number[];
  goals: AnalyticsGoal[];
  max_days: number;
  today: string;
}

export interface AnalyticsMarker { day: string; kind: "audit" | "article" | "fix" | "note" | "google"; text: string; href?: string; url?: string | null; id?: string }

export interface AnalyticsTotals {
  users: number; sessions: number; pageviews: number; conversions: number;
  new_users?: number; engagement_rate?: number; avg_session_seconds?: number; bounce_rate?: number; views_per_visit?: number;
}
export interface AnalyticsRow { name: string; sessions: number; users: number; conversions?: number }

export interface AnalyticsOverview {
  source: AnalyticsSource; days: number; totals: AnalyticsTotals; previous: AnalyticsTotals;
  change: Partial<Record<keyof AnalyticsTotals, number | null>>;
  trend: { date: string; users: number; sessions: number; conversions: number; pageviews?: number; previous_users?: number; previous_sessions?: number }[];
  period: AnalyticsPeriod;
  channels: AnalyticsRow[]; sources: AnalyticsRow[]; countries: AnalyticsRow[]; devices: AnalyticsRow[]; new_vs_returning: AnalyticsRow[];
  markers: AnalyticsMarker[]; note: string;
}

export interface AnalyticsRealtime { active_users: number; countries: { name: string; users: number }[]; pages: { name: string; users: number }[]; minutes: number }

export interface AnalyticsPageRow {
  path: string; url: string; sessions: number; users: number; pageviews: number; conversions: number | null; engagement_rate: number | null; previous_sessions: number;
  clicks: number | null; impressions: number | null; ctr: number | null; position: number | null;
  queries: { query: string; clicks: number; impressions: number; position: number }[];
  content_score: number | null; check_id: string | null; checked_at: string | null; speed_score: number | null; speed_check_id: string | null;
  flags: { key: string; text: string }[];
}
export interface AnalyticsPages { days: number; period: AnalyticsPeriod; source: AnalyticsSource; host: string; rows: AnalyticsPageRow[]; joined: boolean; gsc_connected: boolean; gsc_error: string | null; flagged: number; note: string }

export interface AnalyticsSeo {
  days: number; period: AnalyticsPeriod; source: AnalyticsSource; host: string;
  totals: { sessions: number; users: number; conversions: number }; previous: { sessions: number; users: number; conversions: number };
  share_of_sessions: number; trend: { date: string; sessions: number; conversions: number }[];
  pages: { path: string; sessions: number; conversions: number | null; engagement_rate: number | null; clicks: number | null; impressions: number | null; ctr: number | null; position: number | null; queries: { query: string; clicks: number; impressions: number; position: number }[] }[];
  gsc_connected: boolean; gsc_error: string | null; google: { clicks: number; impressions: number } | null; markers: AnalyticsMarker[];
}

export interface AnalyticsConversions {
  days: number; period: AnalyticsPeriod; source: AnalyticsSource;
  totals: { conversions: number; sessions: number; rate: number }; previous: { conversions: number; sessions: number; rate: number }; change: number | null;
  events: { name: string; conversions: number }[];
  channels: { name: string; conversions: number; sessions: number; rate: number }[];
  pages: { name: string; conversions: number; sessions: number; rate: number }[];
  sources: { name: string; conversions: number; sessions: number; rate: number }[];
  assistants: { name: string; conversions: number; sessions: number; rate: number }[];
  goals: (AnalyticsGoal & { completions: number; previous: number; change: number | null; rate: number })[];
  setup_needed: boolean; note: string;
}

/** The public share link's view (read-only, live numbers). */
export interface SharedAnalytics {
  host: string; source: AnalyticsSource; period: AnalyticsPeriod; shared_at: string; hide_rankbist: boolean;
  overview: AnalyticsOverview; pages: AnalyticsPages | null; conversions: AnalyticsConversions | null;
}

export interface Ga4AiTraffic {
  days: number;
  ai_sessions: number;
  ai_conversions: number;
  all_sessions: number;
  ai_share_percent: number;
  direct_sessions: number;
  note: string;
  assistants: { assistant: string; sessions: number; conversions: number }[];
  pages: { page: string; sessions: number; conversions: number }[];
}

export interface CitationRunSummary {
  id: string;
  status: "running" | "done" | "failed";
  created_at: string;
  prompts_total: number;
  prompts_cited: number;
  prompts_mentioned: number;
  headline: string | null;
  error: string | null;
  note: string;
  engines: Record<string, { name: string; answered: number; cited: number; mentioned: number; failed: number }>;
  most_cited_sources: { domain: string; prompts: number }[];
}

export interface CitationsData {
  project: { domain: string; brand_names: string[]; prompts: string[]; active: boolean; last_run_at: string | null } | null;
  prompt_limit: number;
  engines: string[];
  runs: CitationRunSummary[];
  note: string;
}

// --- Growth & integrations ---

export interface PublicCheckResult {
  overall_score: number;
  category_scores: Record<string, number>;
  category_issue_counts: Record<string, number>;
  word_count: number;
  locked: boolean;
  note: string;
}

export interface ShareState {
  shared: boolean;
  url: string | null;
  views: number;
}

export interface AccessToken {
  id: string;
  name: string;
  prefix: string;
  created_at: string;
  last_used_at: string | null;
}

export interface FixProposal {
  id: string;
  url: string;
  check_id: string | null;
  kind: "meta_title" | "meta_description" | "schema" | "text";
  kind_label: string;
  issue: string;
  before: string;
  after: string;
  why: string;
  status: "pending" | "approved" | "rejected" | "applied";
  created_at: string;
  applied_via: string | null;
  applied_at: string | null;
}

export interface Branding {
  company_name: string;
  color: string;
  contact_email: string;
  contact_phone: string;
  website: string;
  footer_note: string;
  hide_rankbist: boolean;
  has_logo: boolean;
}

export type MemberRole = "editor" | "viewer" | "client" | "custom";
export type AreaLevel = "none" | "view" | "edit";
/** Access per area of the app, e.g. { content: "edit", site: "view", research: "none" }. */
export type AreaLevels = Record<string, AreaLevel>;
export interface WorkspaceArea { key: string; label: string; help: string }

export interface WorkspaceMember {
  id: string;
  email: string;
  role: MemberRole;
  status: "invited" | "active";
  invited_at: string;
  /** What this person can do in each area (worked out from the role, or chosen for "custom"). */
  permissions?: AreaLevels;
}

export interface MultiInviteResult {
  invited: (WorkspaceMember & { workspace_id: string; workspace: string })[];
  already_member: string[];
  email_status: string;
}

export interface WorkspaceInfo {
  id: string;
  name: string;
  site_url: string;
  is_personal: boolean;
  role: "owner" | MemberRole;
  created_at: string;
  members?: WorkspaceMember[];
  owner?: string;
}

export interface WorkspacesData {
  can_manage: boolean;
  workspaces: WorkspaceInfo[];
  joined: WorkspaceInfo[];
  invites: { token: string; workspace: string; role: string }[];
  limits: { workspaces: number; seats: number } | null;
  used: { workspaces: number; seats: number } | null;
  /** Opened inside another workspace: new workspaces are created from your own account. */
  create_from_primary?: boolean;
  /** The areas a custom role chooses access for. */
  areas?: WorkspaceArea[];
}

export interface CreditsData {
  available: boolean;
  balances: Record<string, number>;
  labels: Record<string, string>;
  help?: Record<string, string>; // what each kind covers
  packs: { id: string; name: string; kind: string; kind_label: string; quantity: number; price_cents: number; currency: string }[];
  purchases: { id: string; note: string; kind: string; quantity: number; status: string; created_at: string }[];
}

// --- Phase 12 ---

export interface ImplementationItem {
  key: string;
  label: string;
  marked: boolean;
  verified: boolean | null;
  can_verify: boolean;
  detail: string;
  checked_at: string | null;
  done: boolean;
}

export interface SiteFiles {
  site: { root_url: string; host: string; name: string | null; pages: number; scanned_at: string };
  sitemap: { included: number; excluded: { url: string; reason: string }[]; excluded_count: number; with_date: number; notes: string[]; preview: string };
  robots: { content: string; current: string; had_file: boolean; added: { line: string; reason: string }[]; warnings: string[]; unchanged: boolean; note: string };
  schema: {
    site: Record<string, unknown>[];
    site_tags: string;
    site_checks: { type: string; valid: boolean; placeholders: string[]; problems: string[] }[];
    counts: { pages_with_schema: number; breadcrumbs: number; articles: number; faqs: number };
    notes: string[];
    already_on_home: string[];
    pages: { url: string; types: string[]; tags: string }[];
  };
  features: { guidance: boolean; dev_guides: boolean; meta_ai: boolean; ai_meta_pages: number };
  implementation: ImplementationItem[];
}

export interface GuidanceItem {
  priority: "critical" | "recommended" | "optional";
  area: string;
  title: string;
  detail: string;
}

export interface SiteGuidance {
  counts: Record<"critical" | "recommended" | "optional", number>;
  pages_with_work: number;
  pages_total: number;
  total: number;
  pages: { id: string; url: string; title: string; score: number | null; counts: Record<"critical" | "recommended" | "optional", number>; items: GuidanceItem[] }[];
}

export interface DevGuide {
  platform: string;
  name: string;
  platforms: { key: string; name: string }[];
  review_note: string | null;
  sections: { key: string; title: string; tracks: string[]; automatic: boolean; steps: { text: string; code: string | null; language: string; image: string | null }[] }[];
  implementation: ImplementationItem[];
}

// --- Phase 13: Local SEO ---

export interface BusinessDetails {
  name: string;
  street: string;
  city: string;
  region: string;
  postal_code: string;
  /** ISO country code, e.g. "TH" (US rules apply only to "US"). */
  country?: string;
  phone: string;
  website: string;
  primary_category: string;
  secondary_categories: string[];
  hours: Record<string, string>;
  services: { name: string; description: string }[];
  short_description: string;
  long_description: string;
  service_area: string;
  google_email: string;
  manager_email: string;
}

export interface GbpPack {
  description: string;
  description_length: number;
  description_by: "ai" | "owner" | "";
  description_problems: string[];
  draft_rejected: string[];
  services: { name: string; description: string; trimmed: boolean }[];
  hours: string[];
  name: string;
  address: string;
  phone: string;
  website: string;
  category_terms: string[];
  category_note: string;
  photo_plan: { kind: string; name: string; advice: string }[];
  photo_rules: string;
  generated_at: string;
}

export interface LocalOverview {
  profile: BusinessDetails;
  missing: string[];
  complete: boolean;
  days: [string, string][];
  features: Record<"gbp" | "citations" | "tasks" | "reviews", boolean>;
  pack: GbpPack | null;
  open_tasks: number;
  role: string;
  /** Not required, but each unlocks something (website check, services, owner tasks). */
  recommended?: string[];
}

export interface NapCheck {
  ready: boolean;
  reason?: string;
  needs_scan?: boolean;
  scan_id?: string;
  scanned_at?: string;
  pages_checked?: number;
  consistent?: boolean;
  note?: string;
  items?: { status: "consistent" | "different" | "not_found" | "missing_input"; label: string; expected: string; detail: string; pages: string[]; others: { value: string; pages: string[]; count: number }[] }[];
}

export interface LocalCitations {
  directories: { key: string; name: string; url: string; note: string; for_everyone: boolean; status: "todo" | "listed" | "wrong" }[];
  listed: number;
  total: number;
  nap: { name: string; address: string; phone: string; website: string };
  note: string;
}

export interface LocalTask {
  key: string;
  title: string;
  audience: "client" | "developer";
  why: string;
  status: "pending" | "in_progress" | "completed";
  steps_total: number;
  steps_done: number[];
  guided: boolean;
  link?: string;
  completed_at: string | null;
}

export interface LocalTasks {
  tasks: LocalTask[];
  counts: Record<"pending" | "in_progress" | "completed", number>;
  open: number;
  has_scan: boolean;
  /** The business these tasks are for (one business per workspace). */
  business?: { name: string; website: string; city: string; region: string; complete: boolean; missing: string[] };
}

export interface LocalBusiness {
  /** "" = your own account */
  workspace_id: string;
  workspace: string;
  role: string;
  name: string;
  website: string;
  city: string;
  complete: boolean;
  done: number;
  total: number;
  /** The site named in the business details / when the business was added (may not be in My websites yet). */
  planned_website?: string;
  /** Unread notifications waiting in that business. */
  unread?: number;
  /** This business's own websites (My websites of that workspace). */
  websites?: { id: string; host: string; url: string; label: string; status: "ok" | "problems" | "down" | null }[];
}

export interface LocalBusinesses {
  businesses: LocalBusiness[];
  can_add: boolean;
  add_note: string | null;
  limit: number;
  /** Workspaces this person owns (their own account counts as one). */
  owned?: number;
  /** Workspaces the package allows (-1 = unlimited). */
  allowed?: number;
  /** True while working inside another workspace: new ones are created from your own account. */
  create_from_primary?: boolean;
  /** This person's package includes workspaces (they own and manage them). */
  is_owner?: boolean;
}

export interface LocalGuide {
  key: string;
  title: string;
  audience: string;
  why: string;
  status: string;
  steps_done: number[];
  steps: { text: string; link: string | null; copy: string | null; image: string | null }[];
}

export interface LocalPhotos {
  photos: { id: string; kind: string; kind_name: string; name: string; width: number | null; height: number | null; size_kb: number; warning: string | null }[];
  rules: string;
  limit: number;
  plan: { kind: string; name: string; advice: string; have: number }[];
  missing: string[];
}

export interface ReviewKit {
  link: string;
  ready: boolean;
  templates: { key: string; name: string; subject: string; body: string }[];
  log: { date: string; count: number }[];
  at_start: number | null;
  now: number | null;
  collected: number | null;
  rules: string[];
  note: string;
}

// --- Google indexing (Phase 24) ---------------------------------------------------------------

export type IndexBucket = "indexed" | "not_indexed" | "error" | "excluded" | "unknown";

export interface IndexCounts {
  /** Not indexed only because Google hasn't visited yet (new pages). */
  waiting?: number;
  indexed?: number;
  not_indexed?: number;
  errors?: number;
  excluded?: number;
  unknown?: number;
  total?: number;
  checked?: number;
}

export interface IndexProperty {
  id: string;
  site_url: string;
  label: string;
  host: string;
  website_id: string | null;
  active: boolean;
  health: number | null;
  counts: IndexCounts;
  last_sync_at: string | null;
  remaining_today: number;
  queued: number;
  eta_days: number | null;
  priority: number;
  indexnow: { enabled: boolean; verified_at: string | null };
  quota_hit: boolean;
  state: "running" | "ready";
  /** What the background is doing for this property right now (live progress). */
  activity?: { kind: "sync" | "check" | "sitemaps"; status: string; current: number; total: number; message: string | null } | null;
}

export interface IndexUrlItem {
  id: string;
  url: string;
  path: string;
  priority: boolean;
  source: string;
  in_sitemap: boolean;
  bucket: IndexBucket;
  state: string | null;
  title: string;
  /** null when the package doesn't include Google's reasons (index.reasons) */
  why: string | null;
  action: string | null;
  last_inspected_at: string | null;
  last_crawl_at: string | null;
  queued: boolean;
  locked?: boolean;
}

export interface IndexUrlFull extends IndexUrlItem {
  verdict: string | null;
  indexing_state: string | null;
  robots_state: string | null;
  fetch_state: string | null;
  google_canonical: string | null;
  user_canonical: string | null;
  inspect_link: string | null;
  detail: { crawled_as?: string | null; sitemaps?: string[]; referring?: string[]; mobile?: string | null; rich_results?: string[]; rich_verdict?: string | null };
  first_indexed_at: string | null;
  indexnow_at: string | null;
}

export interface IndexSitemap {
  path: string;
  status: "ok" | "warning" | "error" | "pending";
  errors: number;
  warnings: number;
  urls: number;
  is_index: boolean;
  is_pending: boolean;
  last_submitted: string | null;
  last_read: string | null;
}

export interface IndexBatch {
  id: string;
  total: number;
  done: number;
  issues: number;
  created_at: string;
  finished_at: string | null;
  running: boolean;
}

export interface IndexBatchReport extends IndexBatch {
  counts: { indexed: number; not_indexed: number; error: number; excluded: number; waiting: number; checked: number; in_batch: number };
  indexed_percent: number | null;
  reasons: { state: string; bucket: IndexBucket; count: number; title: string; why: string; action: string }[];
  reasons_locked: boolean;
  can_export: boolean;
}

export interface IndexPropertyFull extends IndexProperty {
  trend: { day: string; health: number | null; indexed: number; errors: number }[];
  recent: IndexUrlItem[];
  problems: IndexUrlItem[];
  reasons: { state: string; bucket: IndexBucket; count: number; title: string; why: string; action: string }[];
  sitemaps: IndexSitemap[];
  batches: IndexBatch[];
  reasons_locked?: boolean;
  history_days?: number;
}

export interface IndexingStatus {
  available: boolean;
  features: Record<"inspect" | "sitemaps" | "coverage" | "bulk" | "monitor" | "indexnow" | "export" | "api" | "reasons" | "sitemap_submit", boolean>;
  connected: boolean;
  needs_reauth: boolean;
  can_write_sitemaps: boolean;
  last_error: string | null;
  limits: {
    index_properties: number; index_inspections_per_day: number; index_priority_urls: number; indexnow_urls_per_day: number;
    index_pages_per_property: number; index_history_days: number; index_bulk_batch_urls: number; indexing_api_urls_per_day: number;
  };
  used_today: number;
  remaining_today: number;
  indexnow_key: string | null;
  properties: IndexProperty[];
}

export interface IndexBulkPreview {
  valid: number;
  outside: number;
  outside_examples: string[];
  too_many: boolean;
  batch_limit: number;
  pages_limit: number;
  tracked: number;
  remaining_today: number;
  already_queued: number;
  per_day: number;
  eta_days: number | null;
}

export interface IndexingApiResult {
  url: string;
  page_type: "job" | "live" | null;
  sent: boolean;
  ok: boolean;
  message: string;
}

export interface IndexingApiStatus {
  in_package: boolean;
  offered: boolean;
  enabled: boolean;
  accepted_at: string | null;
  has_scope: boolean;
  ready: boolean;
  limit: number;
  used_today: number;
  left_today: number;
  terms: string[];
  terms_version: number;
  max_per_request: number;
  recent: (IndexingApiResult & { id: string; kind: "URL_UPDATED" | "URL_DELETED"; created_at: string })[];
}

export interface IndexingApiCheck {
  url: string;
  status: number;
  page_type: "job" | "live" | null;
  eligible: boolean;
  expired: boolean;
  gone: boolean;
  problems: string[];
  summary: string;
}

export interface IndexSettingsOut {
  events: { key: string; label: string; app: boolean; email: boolean; locked?: boolean }[];
  health_threshold: number;
  weekly_day: number;
}
